import { type FC, type PropsWithChildren } from 'react';
import {
    HStack,
    IconOpenInNewTab,
    IconStarFilled,
    Link,
    type TestProps,
    Text,
    TextCell,
    VStack,
} from '@direct-frontend/components';
import {
    type OpenApiGetIntegrationFeedbacksSettingsResponseSuccess,
    type OpenApiSchemaIntegrationType,
    type OpenApiSchemaProductVariantFeedbackInfo,
} from '#shared/api/schema';
import { OZON_SERVICE_NAME } from '#shared/lib/brand';
import { DELIMITER_DOT } from '#shared/lib/constants';
import { getObjectValues } from '#shared/lib/function';
import { settingsFeedbackRoute } from '#shared/lib/routes';
import { format } from '#shared/lib/string';
import { createUrlWithSearchParams } from '#shared/lib/url';
import styles from './ProductVariantFeedbackCell.module.css';

type ProductFeedbackSource = Extract<OpenApiSchemaIntegrationType, 'ozon' | 'yandex_market'>;

const LinkToFeedbackSettings: FC<PropsWithChildren<{ size?: 's' | 'xs' }>> = ({ children, size = 'xs' }) => (
    <Text size={size}>
        <Link
            href={createUrlWithSearchParams(settingsFeedbackRoute.getUrl())}
            color="supplementary"
            target="_blank"
            rel="noreferrer"
        >
            <HStack alignItems="center" gap="4">
                {children}
                <IconOpenInNewTab size="12" />
            </HStack>
        </Link>
    </Text>
);

type ProductVariantFeedbackCellProps = TestProps & {
    feedbackInfo?: OpenApiSchemaProductVariantFeedbackInfo;
    feedbackSettings?: OpenApiGetIntegrationFeedbacksSettingsResponseSuccess;
    onClick?: VoidFunction;
    className?: string;
};

const sourceDefinitions: {
    [key in ProductFeedbackSource]: {
        id: key;
        feedbackSystemTypes: OpenApiSchemaProductVariantFeedbackInfo['system_types'];
        title: string;
    };
} = {
    ozon: {
        id: 'ozon' as const,
        feedbackSystemTypes: ['ozon_sku'] as const,
        title: OZON_SERVICE_NAME,
    },
    yandex_market: {
        id: 'yandex_market' as const,
        feedbackSystemTypes: ['yandex_market'] as const,
        title: 'Маркет',
    },
};

/**
 * Рейтинг варианта и состояния источников отзывов в продуктовых гридах.
 *
 * @kind component
 */
export const ProductVariantFeedbackCell: FC<ProductVariantFeedbackCellProps> = ({
    feedbackInfo,
    feedbackSettings,
    onClick,
    className,
    'data-testid': dataTestId = 'ProductVariantFeedbackCell',
}) => {
    const enabledSources = feedbackSettings
        ? (feedbackSettings.items ?? []).reduce<ProductFeedbackSource[]>((sources, item) => {
              if (item.enabled && (item.system_type === 'yandex_market' || item.system_type === 'ozon')) {
                  sources.push(item.system_type);
              }

              return sources;
          }, [])
        : undefined;

    const visibleSources = getObjectValues(sourceDefinitions);
    const feedbackSystemTypes = new Set(feedbackInfo?.system_types ?? []);
    const sourcesWithFeedback = visibleSources.filter((source) =>
        source.feedbackSystemTypes.some((systemType) => feedbackSystemTypes.has(systemType))
    );
    const sourcesWithoutFeedback = visibleSources.filter((source) => !sourcesWithFeedback.includes(source));
    const noFeedbackContent = (
        <Text size="s" color="gray-disabled">
            Нет данных
        </Text>
    );
    const unconnectedSources = sourcesWithoutFeedback.filter(({ id }) => !enabledSources?.includes(id));
    let emptyContent = null;

    if (enabledSources && enabledSources.length > 0) {
        emptyContent = (
            <VStack gap="4" alignItems="flex-end">
                {noFeedbackContent}
                {unconnectedSources.map(({ id, title }) => (
                    <LinkToFeedbackSettings key={id}>Подключить {title}</LinkToFeedbackSettings>
                ))}
            </VStack>
        );
    } else if (feedbackSettings) {
        emptyContent = <LinkToFeedbackSettings size="s">Подключить</LinkToFeedbackSettings>;
    } else if (!feedbackInfo) {
        emptyContent = noFeedbackContent;
    }

    return (
        <TextCell
            align="right"
            className={className}
            data-testid={dataTestId}
            onClick={onClick}
            title={() => (
                <VStack width="100%" gap="4" alignItems="flex-end">
                    {feedbackInfo && sourcesWithFeedback.length > 0 ? (
                        <>
                            <HStack
                                alignItems="center"
                                justifyContent="flex-end"
                                width="100%"
                                gap="4"
                                className={styles.row}
                                data-testid={`${dataTestId}.Rating`}
                            >
                                <IconStarFilled size="12" color="orange" role="main" />
                                <Text size="s" color="orange">
                                    {format(feedbackInfo.rating, { type: 'number' })}
                                    <span className={styles.count}>
                                        {DELIMITER_DOT}
                                        {format(feedbackInfo.count, { type: 'number' })}
                                    </span>
                                </Text>
                            </HStack>
                            {enabledSources
                                ? sourcesWithoutFeedback.map(({ id, title }) => {
                                      const isEnabled = enabledSources.includes(id);

                                      return (
                                          <HStack
                                              alignItems="center"
                                              justifyContent="flex-end"
                                              width="100%"
                                              gap="4"
                                              className={styles.row}
                                              key={id}
                                              data-testid={`${dataTestId}.Source.${id}`}
                                          >
                                              {isEnabled ? (
                                                  <Text size="s" color="gray-disabled">
                                                      Нет данных
                                                  </Text>
                                              ) : (
                                                  <LinkToFeedbackSettings>Подключить {title}</LinkToFeedbackSettings>
                                              )}
                                          </HStack>
                                      );
                                  })
                                : null}
                        </>
                    ) : (
                        emptyContent
                    )}
                </VStack>
            )}
        />
    );
};
